/**
 * Input Security & Sanitization Utilities
 * Protects against XSS, injection attacks, and malicious content
 */

// Character limits for different field types
export const INPUT_LIMITS = {
  DESCRIPTION: 120,
  NAME: 100,
  SHOP: 100,
  COMMENTS: 1000,
  GENERAL_TEXT: 255,
} as const;

// Allowed characters pattern (restrictive but business-friendly)
export const SECURITY_PATTERNS = {
  // Alphanumeric + common punctuation + spaces + Indian languages support
  DESCRIPTION: /^[\w\s\.,;:!?\-()\/&@#%*+=\u0900-\u097F\u0A00-\u0A7F\u0A80-\u0AFF\u0B00-\u0B7F\u0B80-\u0BFF\u0C00-\u0C7F\u0C80-\u0CFF\u0D00-\u0D7F]*$/,
  
  // More restrictive for names (no special chars except hyphen, apostrophe)
  NAME: /^[\w\s\-'\u0900-\u097F\u0A00-\u0A7F\u0A80-\u0AFF\u0B00-\u0B7F\u0B80-\u0BFF\u0C00-\u0C7F\u0C80-\u0CFF\u0D00-\u0D7F]*$/,
  
  // Basic alphanumeric for general fields
  GENERAL: /^[\w\s\-\u0900-\u097F\u0A00-\u0A7F\u0A80-\u0AFF\u0B00-\u0B7F\u0B80-\u0BFF\u0C00-\u0C7F\u0C80-\u0CFF\u0D00-\u0D7F]*$/,
} as const;

// Dangerous patterns to block (XSS, Script injection, SQL injection)
const DANGEROUS_PATTERNS = [
  /<script/i,
  /<\/script>/i,
  /javascript:/i,
  /on\w+\s*=/i, // onclick, onload, etc.
  /expression\s*\(/i,
  /vbscript:/i,
  /data:text\/html/i,
  /&#/,
  /&lt;script/i,
  /&gt;/i,
  /SELECT.*FROM/i,
  /INSERT.*INTO/i,
  /UPDATE.*SET/i,
  /DELETE.*FROM/i,
  /DROP.*TABLE/i,
  /UNION.*SELECT/i,
  /exec\s*\(/i,
  /eval\s*\(/i,
];

/**
 * Sanitizes and validates text input
 */
export function sanitizeInput(
  input: string,
  type: keyof typeof SECURITY_PATTERNS = 'GENERAL',
  maxLength?: number
): { isValid: boolean; sanitized: string; errors: string[] } {
  const errors: string[] = [];
  let sanitized = input || '';

  // Basic cleanup
  sanitized = sanitized.trim();

  // Check length
  const limit = maxLength || INPUT_LIMITS[type as keyof typeof INPUT_LIMITS] || INPUT_LIMITS.GENERAL_TEXT;
  if (sanitized.length > limit) {
    errors.push(`Text cannot exceed ${limit} characters`);
    sanitized = sanitized.substring(0, limit);
  }

  // Check for dangerous patterns
  for (const pattern of DANGEROUS_PATTERNS) {
    if (pattern.test(sanitized)) {
      errors.push('Text contains potentially dangerous content');
      return { isValid: false, sanitized: '', errors };
    }
  }

  // Check allowed characters
  const allowedPattern = SECURITY_PATTERNS[type];
  if (!allowedPattern.test(sanitized)) {
    errors.push('Text contains invalid characters');
    // Remove invalid characters
    sanitized = sanitized.replace(/[^\w\s\.,;:!?\-()\/&@#%*+=\u0900-\u097F\u0A00-\u0A7F\u0A80-\u0AFF\u0B00-\u0B7F\u0B80-\u0BFF\u0C00-\u0C7F\u0C80-\u0CFF\u0D00-\u0D7F]/g, '');
  }

  // Additional XSS protection - encode HTML entities
  sanitized = sanitized
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');

  return {
    isValid: errors.length === 0,
    sanitized,
    errors
  };
}

/**
 * React hook for input sanitization
 */
export function useSanitizedInput(
  type: keyof typeof SECURITY_PATTERNS = 'GENERAL',
  maxLength?: number
) {
  return {
    onInput: (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const target = e.currentTarget;
      const result = sanitizeInput(target.value, type, maxLength);
      
      if (!result.isValid) {
        target.value = result.sanitized;
        // Could show toast notification for errors
      }
    },
    onBlur: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const target = e.currentTarget;
      const result = sanitizeInput(target.value, type, maxLength);
      target.value = result.sanitized;
    }
  };
}

/**
 * Validation schema helper for secure inputs
 */
export function createSecureTextSchema(
  fieldName: string,
  type: keyof typeof SECURITY_PATTERNS = 'GENERAL',
  required: boolean = false,
  maxLength?: number
) {
  return required
    ? {
        required: `${fieldName} is required`,
        validate: (value: string) => {
          const result = sanitizeInput(value, type, maxLength);
          return result.isValid || result.errors.join(', ');
        }
      }
    : {
        validate: (value: string) => {
          if (!value) return true;
          const result = sanitizeInput(value, type, maxLength);
          return result.isValid || result.errors.join(', ');
        }
      };
}
